import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { pressable } from '../../design/motion'
import { SurfaceCard } from '../../design/SurfaceCard'
import { AnimatedNumber } from '../../design/AnimatedNumber'
import type { SessionTop } from './useMySession'

interface Props {
  /** Today's tops on this wall. Null while loading. */
  tops: SessionTop[] | null
  /** Points earned by today's tops on this wall (see `sessionPoints`). */
  pointsGained: number | null
  /** Your place on this wall's all-time board, or null with none. */
  rank: number | null
  onOpenStandings: () => void
}

/**
 * Today's session in one row of three: tops, points, rank.
 *
 * It replaced a session card per wall plus a separate standings card, which
 * took most of the screen between them. The rank tile is the way into the
 * standings. Logging a top is the floating button, so it needs no room here.
 */
export function SessionStats({ tops, pointsGained, rank, onOpenStandings }: Props) {
  const { t } = useTranslation()
  const count = tops?.length ?? 0
  const points = pointsGained !== null && pointsGained > 0 ? pointsGained : 0

  return (
    <div className="grid grid-cols-3 gap-2" data-testid="session-stats">
      <SurfaceCard className="flex flex-col gap-0.5 px-3 py-2.5">
        <span className="text-2xl font-extrabold leading-tight tracking-tight tabular-nums">
          <AnimatedNumber value={count} />
        </span>
        <span className="truncate text-xs text-foreground/50">{t('session.statTopsToday')}</span>
      </SurfaceCard>
      <SurfaceCard className="flex flex-col gap-0.5 px-3 py-2.5">
        <span
          className={`text-2xl font-extrabold leading-tight tracking-tight tabular-nums ${
            points > 0 ? 'text-success' : ''
          }`}
        >
          {points > 0 ? `+${points.toLocaleString()}` : '0'}
        </span>
        <span className="truncate text-xs text-foreground/50">{t('session.pointsLabel')}</span>
      </SurfaceCard>
      <motion.button
        type="button"
        onClick={onOpenStandings}
        {...pressable}
        data-testid="session-rank"
        className="flex flex-col gap-0.5 rounded-lg bg-foreground px-3 py-2.5 text-left text-background shadow-e2"
      >
        <span className="text-2xl font-extrabold leading-tight tracking-tight tabular-nums">
          {rank !== null ? `#${rank}` : '–'}
        </span>
        <span className="truncate text-xs font-semibold text-[var(--gym-primary)]">
          {t('standings.menuLink')} ›
        </span>
      </motion.button>
    </div>
  )
}
